import PropTypes from 'prop-types'
import styled from 'styled-components/macro'

import { Tag } from '@loadsmart/loadsmart-ui'
import { getToken } from '@loadsmart/loadsmart-ui/dist/theming'

import { useWindowWidth } from 'core/ui/hooks/useWindowWidth'

import { I18n } from 'legacy/components/I18n/i18n'
import { Appointment } from 'legacy/components/LoadOffer/Appointment'
import { Location } from 'legacy/components/LoadOffer/Location'
import { TimelineItem } from 'legacy/components/VerticalTimeline/TimelineItem'
import { screen } from 'legacy/styles/screen'

import { LoadFeatures } from '../constants/LoadOffer'

const Wrapper = styled.section`
  display: flex;
  flex-flow: row wrap;
`

const StopDetails = styled.div`
  display: flex;
  flex-flow: column wrap;
  align-items: flex-start;
  justify-content: flex-start;
  padding-bottom: 8px;
  margin-top: 5px;

  > :not(:last-child) {
    margin-right: 0;
    margin-right: 10px;
    margin-bottom: 3px;
  }

  ${props =>
    !props.forceMobile &&
    `
      ${screen.lg} {
        align-items: center;
        flex-flow: row wrap;
        padding-bottom: 0;

        > :not(:last-child) {
          margin-bottom: 0;
        }
      }
    `};
`

export const LegDistance = styled.span`
  color: ${getToken('color-neutral-dark')};
`

const Zipcode = styled.span`
  color: ${getToken('color-neutral-darkest')};
  font-size: 13px;
  text-transform: uppercase;
`

const FeatureLabel = styled.span`
  display: block;

  color: ${getToken('color-accent')};
  font-weight: ${getToken('font-weight-bold')};
  font-size: ${getToken('font-size-5')};
`

function LoadStop(props) {
  const {
    stop,
    mileage,
    deadhead,
    isPickup,
    isDelivery,
    isVip,
    forceMobile,
    isLoadCandidate,
    isMultiStop,
  } = props

  const { distance_from_previous_stop: leg_distance, tags } = stop

  const isMobile = useWindowWidth() < 1024 || forceMobile
  const showAppointmentTimestamp = !isLoadCandidate
  const isFast = tags?.includes(LoadFeatures.FastFacility)
  const isTopRated = tags?.includes(LoadFeatures.TopRatedFacility)
  const isEarly = tags?.includes(LoadFeatures.EarlyAppointment)
  const isDeadheadLessThan20Miles = deadhead <= 20

  return (
    <Wrapper>
      <TimelineItem
        first={isPickup}
        last={isDelivery}
        color={isVip ? '#575C61' : '#33CC60'}
        top={8}
        margin={{ right: 15 }}
        visible={isMobile}
      >
        <Location
          location={stop}
          mileage={mileage}
          showMileage={isDelivery}
          hideFastFacility={forceMobile}
        />
        <StopDetails forceMobile={forceMobile}>
          {leg_distance === 0 && deadhead > 0 && (
            <Tag variant={isDeadheadLessThan20Miles ? 'success' : 'default'} size="small">
              DH {deadhead}mi
            </Tag>
          )}
          {!isMobile && isEarly && (
            <Tag variant="success" size="small">
              {I18n.get('lb.loadStop.features.earlyAppt')}
            </Tag>
          )}
          <Zipcode>ZIP {stop.zipcode} •</Zipcode>
          {leg_distance > 0 && isMultiStop && (
            <LegDistance>Leg Distance: {leg_distance}mi</LegDistance>
          )}
          <Appointment
            appointment={stop}
            showDate
            showTime={showAppointmentTimestamp}
            highlight={isEarly && !isMobile}
          />
          {isMobile ? (
            <>
              {isEarly && (
                <FeatureLabel>{I18n.get('lb.loadStop.features.earlyAppointment')}</FeatureLabel>
              )}
              {isFast && (
                <FeatureLabel>{I18n.get('lb.loadStop.features.fastFacility')}</FeatureLabel>
              )}
              {isTopRated && (
                <FeatureLabel>{I18n.get('lb.loadStop.features.topRatedFacility')}</FeatureLabel>
              )}
            </>
          ) : null}
        </StopDetails>
      </TimelineItem>
    </Wrapper>
  )
}

LoadStop.propTypes = {
  stop: PropTypes.shape({
    city: PropTypes.string.isRequired,
    state: PropTypes.string.isRequired,
    distance_from_previous_stop: PropTypes.number,
    window_start: PropTypes.string.isRequired,
    window_end: PropTypes.string.isRequired,
    timezone: PropTypes.string,
  }).isRequired,
  isPickup: PropTypes.bool,
  isDelivery: PropTypes.bool,
  isVip: PropTypes.bool,
  forceMobile: PropTypes.bool,
  isLoadCandidate: PropTypes.bool,
  isMultiStop: PropTypes.bool,
  mileage: PropTypes.string,
  deadhead: PropTypes.string,
}

LoadStop.defaultProps = {
  isPickup: false,
  isDelivery: false,
  isVip: false,
  forceMobile: false,
  isLoadCandidate: false,
  isMultiStop: false,
  mileage: '',
  deadhead: null,
}

export { LoadStop }
